import { localNumberFormat } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

type SignDisplay = "auto" | "always" | "exceptZero" | "never";

export interface FormatCurrencyOptions {
  currency?: CurrencyCode;
  decimals?: number;
  signDisplay?: SignDisplay;
}

export const sumFloatingPointNumbers = (a: number, b: number) => trimToDecimals(a + b, 6);

export const sumNumbers = (nums: number[]) => nums.reduce(sumFloatingPointNumbers, 0);

export const formatNumericCurrency = (number: number, { currency = CURRENCY_CODE.EUR, decimals = 2, signDisplay = "auto" }: FormatCurrencyOptions = {}) =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
    signDisplay,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);

/**
 * @deprecated Use `formatNumericCurrency` instead.
 */
export const formatCurrency = (number: number, decimals = 2, signDisplay: SignDisplay = "auto") =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "currency",
    currency: "EUR",
    currencyDisplay: "symbol",
    signDisplay,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);

export const formatCurrencyForAdmin = (number: number, signDisplay: SignDisplay = "auto") =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "currency",
    currency: "EUR",
    currencyDisplay: "symbol",
    signDisplay,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);

export const formatCurrencyRange = (min: number, max: number, options: FormatCurrencyOptions = {}) => {
  if (min === max) {
    return formatNumericCurrency(min, options);
  }

  return `${formatNumericCurrency(min, options)} \u2014 ${formatNumericCurrency(max, options)}`;
};

export const formatNumber = ({ number, decimalPlaces = 2 }: { number: number; decimalPlaces?: number }) =>
  new Intl.NumberFormat(localNumberFormat(), {
    style: "decimal",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(number);

export const formatNumberRange = ({ min, max, decimalPlaces = 2 }: { min: number; max: number; decimalPlaces?: number }) => {
  if (min === max) {
    return formatNumber({ number: min, decimalPlaces });
  }

  return `${formatNumber({ number: min, decimalPlaces })} \u2014 ${formatNumber({ number: max, decimalPlaces })}`;
};

export const formatCO2Amount = (numberOrString: number | string, decimals = 2) => {
  const number = typeof numberOrString === "number" ? numberOrString : parseFloat(numberOrString);

  const formatter = new Intl.NumberFormat(localNumberFormat(), {
    style: "decimal",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${formatter.format(number / 1000)}t`;
};

export const formatCO2AmountRange = (min: number, max: number, decimals = 2) => {
  if (min === max) {
    return formatCO2Amount(min, decimals);
  }

  return `${formatCO2Amount(min, decimals)} - ${formatCO2Amount(max, decimals)}`;
};

export const getVariance = (number1: string | number, number2: number, decimals: number) => {
  const parsedNumber1 = typeof number1 === "string" ? parseFloat(number1) : number1;

  formatNumber({
    number: ((number2 - parsedNumber1) / parsedNumber1) * 100,
    decimalPlaces: decimals,
  });
};

export const generateRandomNumber = (min: number, max: number) => Math.floor(Math.random() * max) + min;

export const truncateCredits = (amount: number) => Math.trunc(amount * 1000) / 1000;

export const normalise = (val: number, max: number, min: number) => (val - min) / (max - min);

export const formatPercentage = (v: number) => parseFloat(v.toFixed(2)) / 100;

export const formatFraction = (v: number) => v * 100;

/**
 * @function isPercentageComplete
 * @description Check if a value is "100%."  We provide a bit of wiggle room
 *  (>= 0.99) here to account for floating point precision issues.
 * @param v - The value to check
 * @returns If v is "100%"
 */
export const isPercentageComplete = (v: number) => v >= 0.99 && v <= 1.001;

export const trimToDecimals = (value: number, decimals: number = 2): number => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

/**
 * @function getYearArray
 * @description Generates an array of years from startYear to endYear (inclusive).
 * @example getYearArray(2020, 2023) => [2020, 2021, 2022, 2023]
 * @param startYear Year to start counting from
 * @param endYear Year to end counting at
 * @returns An array of years (inclusive) from startYear to endYear
 */
export function getYearArray(startYear: number, endYear: number) {
  const years = [];
  for (let i = startYear; i <= endYear; i += 1) {
    years.push(i);
  }
  return years;
}
